'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import type { DashboardData } from '../page';

const HeadcountDonut = dynamic(() => import('./HeadcountDonut'), { ssr: false });
const ShiftDonut = dynamic(() => import('./ShiftDonut'), { ssr: false });

interface Props {
  data: DashboardData;
}

const DEPT_COLORS = [
  'var(--color-blue)',
  'var(--color-green)',
  'var(--primary)',
  'var(--color-purple)',
  'var(--color-amber)',
  'var(--color-red)',
];

export default function KPIRow({ data }: Props) {
  const { employees, payrollRecords, scheduledShifts } = data;

  // Headcount by department
  const deptMap: Record<string, number> = {};
  employees.forEach((e) => {
    const dept = e.department || 'Unassigned';
    deptMap[dept] = (deptMap[dept] || 0) + 1;
  });
  const deptBreakdown = Object.entries(deptMap).map(([dept, count], i) => ({
    dept,
    count,
    color: DEPT_COLORS[i % DEPT_COLORS.length],
  }));

  // Payroll MTD — sum of monthly_gross for active records
  const activePayroll = payrollRecords.filter((p) => p.payroll_status === 'Active');
  const grossMTD = activePayroll.reduce((sum, p) => sum + Number(p.monthly_gross || 0), 0);

  const totalHeadcount = employees.length;

  // Today's shift coverage
  const todayStr = new Date().toISOString().slice(0, 10);
  // Master shifts for today (parent_shift_id is null)
  const todayMasterShifts = scheduledShifts.filter(
    (s) => s.shift_date === todayStr && !s.parent_shift_id
  );
  // Total required = sum of headcount across all positions in each master shift
  const todayTotalRequired = todayMasterShifts.reduce((sum, s) => {
    const positions: any[] = Array.isArray(s.positions_meta) ? s.positions_meta : [];
    const needed = positions.reduce((ps: number, p: any) => ps + (Number(p.headcount) || 1), 0);
    return sum + (needed > 0 ? needed : 1);
  }, 0);
  // Filled = assignment rows for today (parent_shift_id not null, has employee_id)
  const todayFilled = scheduledShifts.filter(
    (s) => s.shift_date === todayStr && s.parent_shift_id && s.employee_id
  ).length;
  const todayUnfilled = Math.max(0, todayTotalRequired - todayFilled);
  const coveragePct = todayTotalRequired > 0 ? Math.round((todayFilled / todayTotalRequired) * 100) : 0;
  const hasShiftData = todayTotalRequired > 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Active Headcount */}
      <div className="card-elevated rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Active Headcount</p>
            <p className="text-4xl font-bold tabular-nums mt-1" style={{ color: 'var(--foreground)' }}>{totalHeadcount}</p>
          </div>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)' }}>
            <Icon name="UserGroupIcon" size={20} className="text-primary" />
          </div>
        </div>
        {deptBreakdown.length > 0 ? (
          <div className="space-y-1.5">
            {deptBreakdown.map((d) => (
              <div key={`dept-${d.dept}`} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: d.color }}></span>
                  <span className="text-xs text-muted-foreground">{d.dept}</span>
                </div>
                <span className="text-xs font-semibold tabular-nums" style={{ color: d.color }}>{d.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No employees yet</p>
        )}
        <Link href="/hr-center" className="flex items-center gap-1 text-xs text-primary hover:underline">
          View HR Center <Icon name="ArrowRightIcon" size={11} />
        </Link>
      </div>

      {/* Today's Shift Coverage */}
      <div className="card-elevated rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Today's Shift Coverage</p>
            <p className="text-4xl font-bold tabular-nums mt-1" style={{ color: 'var(--foreground)' }}>
              {hasShiftData ? `${coveragePct}%` : '—'}
            </p>
          </div>
          <ShiftDonut filled={todayFilled} total={todayTotalRequired} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg p-2.5" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
            <p className="text-xs text-muted-foreground">Filled</p>
            <p className="text-lg font-bold tabular-nums text-color-green">{todayFilled}</p>
          </div>
          <div className="rounded-lg p-2.5" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <p className="text-xs text-muted-foreground">Unfilled</p>
            <p className="text-lg font-bold tabular-nums text-color-red">{todayUnfilled}</p>
          </div>
        </div>
        {hasShiftData ? (
          <p className="text-xs text-muted-foreground">{todayMasterShifts.length} shift{todayMasterShifts.length !== 1 ? 's' : ''} scheduled today</p>
        ) : (
          <p className="text-xs text-muted-foreground">No shifts scheduled for today</p>
        )}
        <Link href="/shift-scheduling" className="flex items-center gap-1 text-xs text-primary hover:underline">
          View Shift Scheduling <Icon name="ArrowRightIcon" size={11} />
        </Link>
      </div>

      {/* Payroll Liability MTD */}
      <div className="card-elevated rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Payroll Liability MTD</p>
            <p className="text-3xl font-bold tabular-nums mt-1" style={{ color: 'var(--foreground)' }}>
              {grossMTD > 0 ? `£${grossMTD.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '£0'}
            </p>
          </div>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.1)' }}>
            <Icon name="BanknotesIcon" size={20} className="text-color-red" />
          </div>
        </div>
        {payrollRecords.length === 0 ? (
          <p className="text-xs text-muted-foreground">No payroll records yet</p>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Active Records</span>
              <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--primary)' }}>{activePayroll.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">On Hold</span>
              <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--color-amber)' }}>
                {payrollRecords.filter((p) => p.payroll_status === 'On Hold').length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Pending</span>
              <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--color-purple)' }}>
                {payrollRecords.filter((p) => p.payroll_status === 'Pending').length}
              </span>
            </div>
          </div>
        )}
        <Link href="/payroll-center" className="flex items-center gap-1 text-xs text-primary hover:underline">
          View Payroll Center <Icon name="ArrowRightIcon" size={11} />
        </Link>
      </div>
    </div>
  );
}